import { memo, useCallback, useEffect, useMemo, useState } from "react"
import {
  Box,
  Button,
  Checkbox as ChakraCheckbox,
  HStack,
  Icon,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { FaMagnifyingGlass, FaCheck, FaXmark } from "react-icons/fa6"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { formatAssetClass } from "@hydrologiq/shared/asset/PaginatedTable"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { useLinkAsset } from "../../hooks/useLinkAsset"
import { useLinkableAssets } from "../../hooks/useLinkableAssets"

// --- Types ---

interface LinkableAssetItem {
  id: string
  name: string
  class: string | null
}

interface LinkAssetDialogProps {
  isOpen: boolean
  onClose: () => void
  assetId: string
  assetClass: string | null
  systemId?: string
  onLinked: () => void
}

// --- Progress Types ---

type LinkProgressStatus = "pending" | "linking" | "done" | "failed"

interface LinkProgressItem {
  id: string
  name: string
  status: LinkProgressStatus
}

// --- Search Field ---

const SearchField = memo(({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
}) => (
  <HStack
    width="100%"
    px={3}
    py={2}
    border="1.5px solid"
    borderColor="#e2e8f0"
    borderRadius="md"
    bg="card"
    gap={2}
  >
    <Icon as={FaMagnifyingGlass}  boxSize={3.5} />
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search by name..."
      disabled={disabled}
      aria-label="Search assets"
      variant="flushed"
      size="sm"
      border="none"
      _focus={{ boxShadow: "none" }}
    />
  </HStack>
))
SearchField.displayName = "SearchField"

// --- Select All Row ---

const SelectAllRow = memo(({
  filteredCount,
  totalSelected,
  allSelected,
  someSelected,
  onToggleAll,
  disabled,
}: {
  filteredCount: number
  totalSelected: number
  allSelected: boolean
  someSelected: boolean
  onToggleAll: () => void
  disabled?: boolean
}) => (
  <HStack
    width="100%"
    px={3}
    py={2}
    borderBottom="1.5px solid"
    borderColor="#e2e8f0"
    justify="space-between"
    bg="bg.subtle"
  >
    <HStack gap={2}>
      <ChakraCheckbox.Root
        checked={allSelected ? true : someSelected ? "indeterminate" : false}
        onCheckedChange={onToggleAll}
        disabled={disabled}
      >
        <ChakraCheckbox.HiddenInput />
        <ChakraCheckbox.Control />
      </ChakraCheckbox.Root>
      <Text fontSize="sm" fontWeight="medium">Select all</Text>
    </HStack>
    <Text fontSize="xs" color="fg.muted">
      {filteredCount} available · {totalSelected} selected
    </Text>
  </HStack>
))
SelectAllRow.displayName = "SelectAllRow"

// --- Asset List Item ---

const AssetListItem = memo(({
  asset,
  checked,
  onToggle,
  disabled,
}: {
  asset: LinkableAssetItem
  checked: boolean
  onToggle: () => void
  disabled?: boolean
}) => (
  <HStack
    width="100%"
    px={3}
    py={3}
    cursor={disabled ? "default" : "pointer"}
    bg={checked ? "#eaf7fa" : "transparent"}
    _hover={disabled ? undefined : { bg: checked ? "#eaf7fa" : "gray.50" }}
    onClick={onToggle}
    borderBottom="1px solid"
    borderColor="#e2e8f0"
    gap={3}
  >
    <ChakraCheckbox.Root
      checked={checked}
      onCheckedChange={onToggle}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
    >
      <ChakraCheckbox.HiddenInput />
      <ChakraCheckbox.Control />
    </ChakraCheckbox.Root>

    <VStack align="start" gap={0} flex={1} minWidth={0}>
      <Text fontSize="sm" fontWeight="medium" truncate>{asset.name}</Text>
      {asset.class && (
        <Text fontSize="xs" color="fg.muted" truncate>{formatAssetClass(asset.class)}</Text>
      )}
    </VStack>
  </HStack>
))
AssetListItem.displayName = "AssetListItem"

// --- Progress Screen ---

const LinkProgressScreen = memo(({ items }: { items: LinkProgressItem[] }) => (
  <VStack align="stretch" gap={3} width="100%" py={4}>
    <Text fontSize="sm" fontWeight="medium" color="fg.muted">
      Linking assets...
    </Text>
    <VStack align="stretch" gap={2} px={2}>
      {items.map((item) => (
        <HStack key={item.id} gap={3} py={1}>
          {item.status === "linking" && <Spinner size="xs" color="#39aed2" />}
          {item.status === "done" && <Icon as={FaCheck} boxSize={3} color="green.500" />}
          {item.status === "failed" && <Icon as={FaXmark} boxSize={3} color="red.500" />}
          {item.status === "pending" && <Box width="12px" height="12px" borderRadius="full" bg="gray.200" />}
          <Text fontSize="sm" color={item.status === "pending" ? "fg.muted" : "fg.default"}>
            {item.name}
          </Text>
          {item.status === "failed" && (
            <Text fontSize="xs" color="red.500" ml="auto">Failed</Text>
          )}
        </HStack>
      ))}
    </VStack>
  </VStack>
))
LinkProgressScreen.displayName = "LinkProgressScreen"

// --- Main Dialog ---

/**
 * Dialog for linking unassigned assets as children of the current asset.
 * Mirrors the "Add asset to system" UX pattern from the systems section.
 * Only shows assets that have no system assignment and no parent.
 * For non-hybrid parents, also filters by matching class. Hybrid parents show all classes.
 */
export function LinkAssetDialog({ isOpen, onClose, assetId, assetClass, systemId, onLinked }: LinkAssetDialogProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState("")
  const [showProgress, setShowProgress] = useState(false)
  const [progressItems, setProgressItems] = useState<LinkProgressItem[]>([])
  const [submitting, setSubmitting] = useState(false)

  const { linkAsset } = useLinkAsset()
  const { fetchLinkableAssets, linkableAssets, loading: loadingAssets } = useLinkableAssets()

  // Fetch linkable assets when dialog opens, filtered by class (and system if available)
  // Hybrid parents can contain children of any class, so skip the class filter
  useEffect(() => {
    if (isOpen) {
      const isHybrid = assetClass?.toUpperCase() === "HYBRID"
      fetchLinkableAssets({
        existingParent: assetId,
        assetClass: isHybrid ? undefined : (assetClass ?? undefined),
        system: systemId,
      })
    }
  }, [isOpen, assetId, assetClass, systemId, fetchLinkableAssets])

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedIds(new Set())
      setSearch("")
      setShowProgress(false)
      setProgressItems([])
      setSubmitting(false)
    }
  }, [isOpen])

  // Map to display format
  const assets: LinkableAssetItem[] = useMemo(
    () => linkableAssets.map((a) => ({ id: a.id, name: a.name, class: a.class })),
    [linkableAssets],
  )

  // Filter by search
  const filteredAssets = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return assets
    return assets.filter((a) => a.name.toLowerCase().includes(term))
  }, [assets, search])

  // Selection helpers
  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const allSelected = filteredAssets.length > 0 && filteredAssets.every((a) => selectedIds.has(a.id))
  const someSelected = filteredAssets.some((a) => selectedIds.has(a.id)) && !allSelected

  const handleToggleAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredAssets.map((a) => a.id)))
    }
  }, [allSelected, filteredAssets])

  // Submit — link each selected asset with progress
  const handleConfirm = useCallback(async () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return

    const items: LinkProgressItem[] = ids.map((id) => {
      const asset = assets.find((a) => a.id === id)
      return { id, name: asset?.name || id, status: "pending" as LinkProgressStatus }
    })

    setProgressItems(items)
    setShowProgress(true)
    setSubmitting(true)

    let successCount = 0
    for (let i = 0; i < ids.length; i++) {
      setProgressItems((prev) => prev.map((item, idx) => idx === i ? { ...item, status: "linking" } : item))
      try {
        await linkAsset(ids[i], assetId)
        setProgressItems((prev) => prev.map((item, idx) => idx === i ? { ...item, status: "done" } : item))
        successCount++
      } catch {
        setProgressItems((prev) => prev.map((item, idx) => idx === i ? { ...item, status: "failed" } : item))
      }
    }

    setSubmitting(false)

    if (successCount > 0) {
      toaster.success({ title: `${successCount} asset(s) linked successfully` })
    }

    // Brief delay to show final state then close
    setTimeout(() => {
      onLinked()
    }, 800)
  }, [selectedIds, assets, linkAsset, assetId, onLinked])

  const handleClose = useCallback(() => {
    if (!submitting) onClose()
  }, [submitting, onClose])

  const selectedCount = selectedIds.size

  return (
    <Dialog
      title="Link assets"
      description={showProgress ? undefined : "Select assets to link as modules of this asset"}
      open={isOpen}
      setOpen={(open) => {
        if (!open) handleClose()
      }}
      onClose={handleClose}
      size="lg"
      showCloseButton={!submitting}
      footer={
        showProgress ? undefined : (
          <HStack width="100%" justify="space-between" py={2}>
            <HStack gap={2}>
              <Box
                bg="#39aed2"
                color="white"
                borderRadius="full"
                width="28px"
                height="28px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="xs"
                fontWeight="bold"
              >
                {selectedCount}
              </Box>
              <Text fontSize="sm" fontWeight="medium">assets selected</Text>
            </HStack>
            <HStack gap={2}>
              <Button variant="hydrologiq_secondary" size="sm" onClick={handleClose} disabled={submitting} borderRadius="5px">
                Cancel
              </Button>
              <Button
                variant="hydrologiq_primary"
                size="sm"
                onClick={handleConfirm}
                disabled={submitting || selectedCount === 0}
                borderRadius="5px"
              >
                Link
              </Button>
            </HStack>
          </HStack>
        )
      }
    >
      <VStack align="stretch" gap={0} width="100%" minHeight="350px">
        {showProgress ? (
          <LinkProgressScreen items={progressItems} />
        ) : loadingAssets ? (
          <HStack justify="center" p={6}>
            <Spinner size="sm" />
            <Text fontSize="sm" >Loading available assets...</Text>
          </HStack>
        ) : assets.length === 0 ? (
          <VStack p={6} gap={2}>
            <Text fontSize="sm">No available assets</Text>
            <Text fontSize="xs">
              {assetClass?.toUpperCase() === "HYBRID"
                ? "No unassigned assets are available for linking."
                : `No unassigned assets of this asset type (${formatAssetClass(assetClass)}) are available for linking.`}
            </Text>
          </VStack>
        ) : (
          <>
            <SearchField value={search} onChange={setSearch} disabled={submitting} />
            <Box mt={3} border="1.5px solid" borderColor="#e2e8f0" borderRadius="md" overflow="hidden" bg="card">
              <SelectAllRow
                filteredCount={filteredAssets.length}
                totalSelected={selectedCount}
                allSelected={allSelected}
                someSelected={someSelected}
                onToggleAll={handleToggleAll}
                disabled={submitting}
              />
              <VStack align="stretch" gap={0} maxHeight="320px" overflowY="auto">
                {filteredAssets.length === 0 ? (
                  <Text fontSize="sm"  py={4} px={3}>No assets match your search.</Text>
                ) : (
                  filteredAssets.map((asset) => (
                    <AssetListItem
                      key={asset.id}
                      asset={asset}
                      checked={selectedIds.has(asset.id)}
                      onToggle={() => toggleSelected(asset.id)}
                      disabled={submitting}
                    />
                  ))
                )}
              </VStack>
            </Box>
          </>
        )}
      </VStack>
    </Dialog>
  )
}

export default LinkAssetDialog
